/**
 * QueBoard (깨보드) Types
 */

// --- Unit (인형) ---

export type UnitState = 'off' | 'on' | 'select' | 'done'

export interface UnitData {
  col: number
  row: number
  state: UnitState
  container: 'map' | 'bank'
  isEmpty?: boolean // bank [3][2] only
}

export interface Coordinate {
  col: number
  row: number
}

// --- Side Indicator (묶음 표시등) ---

export type SideState = 'off' | 'full'

export type SideDirection = 'x' | 'y'

export interface SideData {
  index: number
  direction: SideDirection
  state: SideState
  isMerged: boolean
  mergedWith?: number // 합쳐진 파트너 인덱스 (release 시 복원용)
}

// --- Drag ---

export type DragModeName = 'tie' | 'borrow' | 'place' | 'return'

export interface DragRect {
  startCol: number
  startRow: number
  endCol: number
  endRow: number
}

export interface DragState {
  mode: DragModeName
  active: boolean
  target: 'map' | 'bank' | 'side' | null
  rect: DragRect | null
  startUnit: Coordinate | null
  endUnit: Coordinate | null
}

// --- Problem ---

export interface Problem {
  left: number
  right: number
}

// --- Answer Check ---

export interface AnswerCheckResult {
  isCorrect: boolean
  tens: number
  ones: number
  expected: number
  failReason?: string
}

// --- Character ---

export type CharacterId = 'queda' | 'chaeli' | 'ddami' | 'random'

// --- QueBoardConfig (기능 토글 + 튜터리얼 설정) ---

export type TutorialModuleId = 'teleport10' | 'teleport5' | 'fatTie' | 'bank'

export interface QueBoardFeatures {
  tie: boolean
  teleport5: boolean
  teleport10: boolean
  bank: boolean
  indicator: boolean
  customInput: boolean
}

export interface TutorialConfig {
  enabled: boolean
  modules: TutorialModuleId[]
  autoStart: boolean
}

export interface QueBoardConfig {
  problem: Problem
  allowCustomInput: boolean
  features: QueBoardFeatures
  tutorial: TutorialConfig
  character?: CharacterId
}

export const DEFAULT_FEATURES: QueBoardFeatures = {
  tie: true,
  teleport5: true,
  teleport10: true,
  bank: true,
  indicator: true,
  customInput: false,
}

// --- Tutorial (런타임 정의) ---

export interface HighlightArea {
  type: 'map' | 'bank' | 'side' | 'mode-selector' | 'toolbar' | 'teleport-btn'
  startCol?: number
  startRow?: number
  endCol?: number
  endRow?: number
  sideDir?: SideDirection
  sideIndex?: number
  buttonId?: 'row5' | 'row10' | 'col5' | 'col10'
}

export interface TutorialValidationState {
  mapGrid: UnitData[][]
  bankGrid: UnitData[][]
  xSides: SideData[]
  ySides: SideData[]
  fullCount: number
  looseUnits: { col: number; row: number }[]
  currentMode: DragModeName
  completed: boolean
}

export interface TutorialSetupActions {
  showProblem: (problem: Problem) => void
  resetSides: () => void
  setUnitState: (container: 'map' | 'bank', col: number, row: number, state: UnitState) => void
  setRangeState: (container: 'map' | 'bank', startCol: number, startRow: number, endCol: number, endRow: number, state: UnitState) => void
  tieGroup: (dir: SideDirection, index: number, size: number) => void
  mergeSides: (dir: SideDirection, index1: number, index2: number) => void
}

export interface TutorialStep {
  id: string
  instruction: string
  hint?: string
  highlightArea?: HighlightArea
  requiredMode?: DragModeName
  validate: (state: TutorialValidationState) => boolean
  autoSetup?: (actions: TutorialSetupActions) => void
}

export interface TutorialModule {
  id: TutorialModuleId
  name: string
  description: string
  problem: Problem
  steps: TutorialStep[]
}

// --- Session Result (네비게이션 상태) ---

export interface QueBoardResult {
  completed: boolean
  answer?: number
  problem?: Problem
}

// --- Teleport State Machine ---

export interface TeleportState {
  phase: 'idle' | 'selecting' | 'rotating' | 'placing'
  axis: 'row' | 'col' | null
  targetLength: number | null
  /** 시스템이 계산한 추천 행/열 (가이드 표시용) */
  recommendedLines: number[]
  /** 추천 셀 좌표 (가이드 하이라이트용) */
  recommendedCells: { col: number; row: number }[]
  /** 사용자가 드래그로 선택 완료한 행/열 */
  selectedLines: number[]
  /** 선택 완료된 셀 좌표 (원위치 복원용) */
  selectedCells: { col: number; row: number }[]
  /** 회전 후 상대 좌표 (placing용, 앵커 기준 오프셋) */
  ghostCells: { col: number; row: number }[]
  /** 마우스가 가리키는 셀 좌표 (placing 중 고스트 앵커) */
  ghostAnchor: { col: number; row: number } | null
}

// --- Props ---

export interface QueBoardProps {
  isOpen: boolean
  onClose: () => void

  /** 수식 설정 (외부에서 주입 시) */
  problem?: Problem

  /** 자유 입력 모드 (problem 없으면 자동 활성) */
  allowCustomInput?: boolean

  /** 완료 콜백 */
  onComplete?: (result: { problem: [number, number]; answer: number }) => void

  /** 캔버스 크기 */
  width?: number
  height?: number

  /** 통합 설정 (제공 시 problem/allowCustomInput보다 우선) */
  config?: QueBoardConfig

  /** 튜터리얼 모듈 (config.tutorial 설정과 함께 사용) */
  tutorialModules?: TutorialModule[]

  /** 상단 라운드 제거 (플로팅 윈도우 모드) */
  noTopRadius?: boolean

  /** 외부에서 증가시키면 내부 상태를 리셋 (언마운트 없이) */
  resetKey?: number
}
